import React from 'react';
import { Button, Card } from 'antd';
import styled from '@emotion/styled';
import { InvestigationRequest } from 'vl-common/src/schemas/shared/investigationRequest';

export const ProviderCardSkeleton = styled.div`
  background: #fff;
  display: grid;
  grid-template-columns: repeat(2, 2fr);
  grid-gap: 16px;

  @keyframes loading {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      opacity: 1;
    }
  }

  > div {
    background: rgba(0, 0, 0, 0.05);
    animation: loading 1.5s infinite;
    border-radius: 2px;
    min-height: 170px;
  }

  @media (max-width: 992px) {
    grid-template-columns: repeat(1, 4fr);
    height: 1200px;
  }
`;

interface ProviderProps {
  addline1: string;
  addline2: string;
  addline3: string;
  addline4: string;
  distance_units: string;
  distance: string;
  group_name: string;
  hospital_id: number;
  hospital_name: string;
  postcode: string;
  region: string;
  service_type_codes: string;
  service_type_code: string;
}

interface ProviderCardProps {
  hospitalID: number;
  isCompleted: boolean;
  provider: ProviderProps | InvestigationRequest;
  setHospitalID: (hospitalID: number) => void;
  confirmInvestigationModalView: boolean;
  stepIsCompleting?: boolean;
}

// Type guard to check if provider is of type ProviderProps
const isProviderProps = (provider: ProviderProps | InvestigationRequest): provider is ProviderProps => {
  return (provider as ProviderProps).distance_units !== undefined;
};

const ProviderCard = ({
  provider,
  hospitalID,
  isCompleted = false,
  setHospitalID,
  confirmInvestigationModalView = false,
  stepIsCompleting = false
}: ProviderCardProps) => {
  const {
    addline1,
    addline2,
    addline3,
    addline4,
    group_name,
    hospital_id,
    hospital_name,
    postcode,
    region,
    service_type_code
  } = provider;
  const address = [addline1, addline2, addline3, addline4, region, postcode].filter((item) => item).join(', ');
  const isSelected = hospitalID === hospital_id;

  let description = '';
  if (confirmInvestigationModalView) {
    description = `${service_type_code} - ${address}`;
  } else if (isProviderProps(provider)) {
    description = `${provider.service_type_codes} - ${address} - ${provider.distance} ${provider.distance_units}`;
  }

  return (
    <Card
      title={group_name}
      size="small"
      extra={
        !confirmInvestigationModalView && (
          <Button
            type="primary"
            disabled={isCompleted || isSelected || stepIsCompleting}
            onClick={() => setHospitalID(Number(hospital_id))}
            data-testid="select-provider"
          >
            {isSelected ? 'Selected' : 'Select'}
          </Button>
        )
      }
      style={{
        minHeight: '170px'
      }}
    >
      <Card.Meta title={hospital_name} description={description} />
    </Card>
  );
};

export default ProviderCard;
export { ProviderCard };
